import { useEffect } from 'react';
import {
  Sprout, Handshake, Lightbulb, Target, Music, Dumbbell,
  ArrowRight, MapPin, Phone, Mail, CheckCircle, Sun, Medal
} from 'lucide-react';

const GF = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=DM+Sans:wght@300;400;500;600&display=swap';

const VALUES = [
  { icon: Sprout,    name: 'Nurturing Environment', desc: 'Small class sizes ensure every child receives personal attention and feels seen every day.' },
  { icon: Handshake, name: 'Inclusive Community',   desc: 'We welcome children of all backgrounds, building empathy and respect from an early age.' },
  { icon: Lightbulb, name: 'Curious Minds',         desc: 'Project-based learning encourages questioning, exploration, and a love of discovery.' },
  { icon: Target,    name: 'Academic Strength',     desc: 'CBSE-aligned curriculum with enrichment activities developing critical thinking.' },
  { icon: Music,     name: 'Arts & Culture',        desc: 'Music, dance, visual arts, and drama programmes let every child\'s creative spirit shine.' },
  { icon: Dumbbell,  name: 'Active Bodies',         desc: 'Daily physical education, yoga, and interschool sports build fitness and teamwork.' },
];

const GRADES = [
  { icon: '🌸', bg: 'bg-terra-pale', name: 'Nursery & KG',          desc: 'Play-based, sensory-rich early learning' },
  { icon: '📚', bg: 'bg-green-50',   name: 'Primary (I–V)',          desc: 'Building strong foundations with joy' },
  { icon: '🔭', bg: 'bg-amber-50',   name: 'Middle (VI–VIII)',       desc: 'Inquiry, projects, and self-discovery' },
  { icon: '🎓', bg: 'bg-terra-pale', name: 'Secondary (IX–X)',       desc: 'CBSE board preparation with full support' },
];

export default function WarmTheme({ school }) {
  const loginUrl = `/login?school=${school.slug}`;

  useEffect(() => {
    const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: GF });
    document.head.appendChild(link);
    document.title = `${school.name} | EduPulse`;
    return () => document.head.removeChild(link);
  }, [school.name]);

  const achievements = [
    `${school.stats.results} students score above 75% in ${school.board} boards`,
    `${school.stats.staff}+ dedicated and trained faculty members`,
    'State-level sports champions across multiple disciplines',
    'Ranked among Top Schools by EduRank India',
    `Active alumni network of ${school.stats.alumni} across India`,
  ];

  return (
    <div className="font-dm bg-[#fdf8f2] text-brown overflow-x-hidden">

      {/* ── NAV ── */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-[#fdf8f2] border-b border-brown/10 h-[70px] flex items-center justify-between px-13">
        <div className="flex items-center gap-3">
          <div className="w-[38px] h-[38px] bg-terra rounded-full flex items-center justify-center">
            <Sun size={18} className="text-white" />
          </div>
          <span className="font-fraunces text-[18px] font-semibold text-brown">{school.name}</span>
        </div>
        <ul className="hidden md:flex gap-8 list-none">
          {['About', 'Classes', 'Events', 'Contact'].map(l => (
            <li key={l}>
              <a href={`#${l.toLowerCase()}`} className="text-[15px] text-brown-muted hover:text-terra transition-colors no-underline">{l}</a>
            </li>
          ))}
        </ul>
        <a href={loginUrl}
          className="text-[14px] font-semibold bg-terra text-white px-6 py-2.5 rounded-full hover:bg-terra-light transition-colors no-underline">
          Login to Portal
        </a>
      </nav>

      {/* ── HERO ── */}
      <section className="min-h-screen bg-sand pt-[130px] pb-20 px-13 grid md:grid-cols-2 gap-16 items-center relative overflow-hidden">
        <div className="absolute top-[-80px] right-[-80px] w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(193,68,14,0.12),transparent_70%)] rounded-full" />

        <div>
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-2.5 h-2.5 bg-terra rounded-full" />
            <span className="text-[14px] font-medium text-terra tracking-[0.05em]">
              Admissions Open for {new Date().getFullYear()}–{String(new Date().getFullYear() + 1).slice(2)}
            </span>
          </div>
          <h1 className="font-fraunces text-[clamp(44px,5vw,68px)] font-semibold leading-[1.15] text-brown mb-6">
            A Place Where <em className="italic text-terra">Every Child</em> Belongs
          </h1>
          <p className="text-[17px] text-brown-muted leading-[1.8] max-w-md mb-10">
            {school.name} believes every child is a unique learner. We nurture curiosity,
            kindness, and confidence through a warm, inclusive environment where children truly thrive.
          </p>
          <div className="flex gap-3.5 items-center mb-14 flex-wrap">
            <a href={loginUrl}
              className="text-[15px] font-semibold bg-terra text-white px-7 py-3.5 rounded-full hover:bg-terra-light transition-all hover:-translate-y-0.5 no-underline">
              Login to Portal
            </a>
            <a href="#about"
              className="text-[15px] font-medium text-brown hover:text-terra transition-colors no-underline flex items-center gap-1.5">
              Meet Our Teachers <ArrowRight size={15} />
            </a>
          </div>
          {/* testimonial */}
          <div className="bg-white rounded-[20px] p-6 flex gap-3.5 items-start max-w-sm shadow-[0_4px_24px_rgba(61,43,31,0.08)]">
            <div className="w-11 h-11 bg-terra-pale rounded-full flex items-center justify-center text-xl shrink-0">👩</div>
            <div>
              <p className="font-fraunces italic text-[15px] text-brown leading-[1.6] mb-2">
                "My daughter has blossomed here. The teachers genuinely care about every child."
              </p>
              <span className="text-[13px] text-brown-muted">— Parent of Grade 4 student</span>
            </div>
          </div>
        </div>

        {/* hero card */}
        <div className="relative hidden md:block">
          <div className="bg-terra rounded-[28px] p-12 h-[420px] flex flex-col justify-end relative overflow-hidden">
            <div className="absolute top-10 right-10 text-[80px] opacity-30 select-none">☀️</div>
            <div className="text-[13px] text-white/60 mb-2 font-medium">A community that cares</div>
            <div className="font-fraunces text-[28px] font-semibold text-white leading-[1.3]">
              Shaping hearts and minds since {school.establishedYear}
            </div>
            <div className="flex gap-8 mt-6">
              <div>
                <div className="font-fraunces text-[28px] font-bold text-white">{school.stats.students}</div>
                <div className="text-[12px] text-white/60">Students</div>
              </div>
              <div>
                <div className="font-fraunces text-[28px] font-bold text-white">{school.stats.results}</div>
                <div className="text-[12px] text-white/60">Satisfaction Rate</div>
              </div>
            </div>
          </div>
          {/* mini card */}
          <div className="absolute -bottom-6 -left-6 bg-white rounded-[20px] px-6 py-5 shadow-[0_8px_32px_rgba(61,43,31,0.12)] flex items-center gap-3.5">
            <div className="w-11 h-11 bg-sage rounded-xl flex items-center justify-center">
              <Medal size={20} className="text-white" />
            </div>
            <div>
              <div className="font-fraunces text-[22px] font-bold text-brown">State Rank #1</div>
              <div className="text-[12px] text-brown-muted">Best Value School {new Date().getFullYear() - 1}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="py-24 px-13 bg-[#fdf8f2]" id="about">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <div className="text-[13px] font-semibold text-terra tracking-[0.08em] uppercase mb-3">What We Stand For</div>
              <h2 className="font-fraunces text-[clamp(32px,3vw,46px)] font-semibold text-brown">
                Education with <em className="italic text-terra">Heart</em>
              </h2>
            </div>
            <p className="text-[16px] text-brown-muted max-w-xs text-right leading-[1.7]">
              We go beyond books to nurture children who are compassionate, confident, and ready for life.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map(({ icon: Icon, name, desc }) => (
              <div key={name} className="bg-white rounded-[24px] p-9 hover:shadow-[0_12px_48px_rgba(61,43,31,0.1)] transition-shadow">
                <div className="w-12 h-12 bg-terra-pale rounded-xl flex items-center justify-center mb-5">
                  <Icon size={22} className="text-terra" />
                </div>
                <div className="font-fraunces text-[22px] font-semibold text-brown mb-2.5">{name}</div>
                <p className="text-[15px] text-brown-muted leading-[1.7]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACADEMICS ── */}
      <section className="py-24 px-13 bg-sand" id="classes">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-20 items-start">
          <div>
            <div className="text-[13px] font-semibold text-terra tracking-[0.08em] uppercase mb-3">Our Programmes</div>
            <h2 className="font-fraunces text-[clamp(32px,3vw,46px)] font-semibold text-brown mb-10">
              Learning for every <em className="italic text-terra">stage</em>
            </h2>
            <div className="flex flex-col gap-3">
              {GRADES.map(g => (
                <div key={g.name}
                  className="bg-white rounded-2xl px-6 py-5 flex items-center gap-4 border-2 border-transparent hover:border-terra transition-all group cursor-default">
                  <div className={`w-11 h-11 ${g.bg} rounded-xl flex items-center justify-center text-xl shrink-0`}>{g.icon}</div>
                  <div className="flex-1">
                    <div className="font-fraunces text-[17px] font-semibold text-brown">{g.name}</div>
                    <div className="text-[13px] text-brown-muted mt-0.5">{g.desc}</div>
                  </div>
                  <ArrowRight size={18} className="text-brown-muted group-hover:text-terra group-hover:translate-x-1 transition-all" />
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[13px] font-semibold text-terra tracking-[0.08em] uppercase mb-3">
              Why {school.name.split(' ')[0]}
            </div>
            <h2 className="font-fraunces text-[clamp(32px,3vw,46px)] font-semibold text-brown mb-5">
              Results that <em className="italic text-terra">speak</em> for themselves
            </h2>
            <p className="text-[16px] text-brown-muted leading-[1.8] mb-8">
              Our students consistently excel — not just in academics, but in character,
              confidence, and readiness for life's next chapter.
            </p>
            <div className="flex flex-col gap-4">
              {achievements.map((a, i) => (
                <div key={i} className="flex items-center gap-3.5 text-[15px] text-brown">
                  <CheckCircle size={22} className="text-sage shrink-0" />
                  {a}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-13 bg-terra">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-fraunces text-[clamp(36px,4vw,54px)] font-semibold text-white leading-[1.2] mb-5">
            Give your child the <em className="italic">start they deserve</em>
          </h2>
          <p className="text-[17px] text-white/75 leading-[1.8] mb-12">
            Join the {school.name.split(' ')[0]} family and watch your child grow in
            confidence, curiosity, and character.
          </p>
          <div className="flex gap-3.5 justify-center flex-wrap">
            <a href={loginUrl}
              className="text-[15px] font-semibold bg-white text-terra px-7 py-3.5 rounded-full hover:bg-[#fdf8f2] transition-all hover:-translate-y-0.5 no-underline">
              Login to Portal
            </a>
            <a href="#contact"
              className="text-[15px] font-medium text-white border-2 border-white/40 px-7 py-3.5 rounded-full hover:border-white transition-colors no-underline">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-brown py-18 px-13" id="contact">
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-16 mb-14">
          <div>
            <div className="font-fraunces text-[22px] font-semibold text-white mb-2">{school.name}</div>
            <div className="font-fraunces italic text-[16px] text-white/40 mb-5">{school.motto}</div>
            <div className="space-y-2 text-[14px] text-white/35">
              <div className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 shrink-0" />{school.address}</div>
              <div className="flex items-center gap-2"><Phone size={13} />{school.phone}</div>
              <div className="flex items-center gap-2"><Mail size={13} />{school.email}</div>
            </div>
          </div>
          {[
            { h: 'School',   l: ['About Us', 'Our Team', 'Campus', 'Achievements'] },
            { h: 'Learning', l: ['Curriculum', 'Activities', 'Library', 'Results'] },
            { h: 'Family',   l: ['Admissions', 'Fee Details', 'Parent Portal', 'Contact Us'] },
          ].map(col => (
            <div key={col.h}>
              <h4 className="text-[13px] font-semibold text-white/50 uppercase tracking-[0.1em] mb-5">{col.h}</h4>
              <ul className="space-y-3 list-none">
                {col.l.map(l => (
                  <li key={l}><a href="#contact" className="text-[14px] text-white/40 hover:text-terra-light transition-colors no-underline">{l}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/8 pt-7 flex flex-col md:flex-row justify-between text-[13px] text-white/20">
          <span>© {new Date().getFullYear()} {school.name}. All rights reserved.</span>
          <span>{school.affiliation}</span>
        </div>
      </footer>
    </div>
  );
}